import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Spin,
  Tooltip,
  Upload,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoonOutlined,
  SunOutlined,
  LogoutOutlined,
  LinkOutlined,
  VideoCameraOutlined,
  LoadingOutlined,
  ColumnWidthOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setOffices,
  updateOffice as updateOfficeAction,
} from '../store/slices/exchangeOfficeSlice';
import { logout } from '../store/slices/authSlice';
import { ExchangeOffice, ExchangeOfficeRequest, MediaObject } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import * as api from '../services/api';
import { ColumnType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload/interface';

const { Header, Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

// Helper function to extract URL from media object or string
const getMediaUrl = (media: MediaObject | string | null | undefined): string => {
  if (!media) return '';
  if (typeof media === 'string') return media;
  return media.url || '';
};

// Helper function to extract ID from media object
const getMediaId = (media: MediaObject | string | null | undefined): number | null => {
  if (!media) return null;
  if (typeof media === 'object' && media.id) return media.id;
  return null;
};

const CentralAdmin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { offices } = useSelector((state: any) => state.exchangeOffice);
  const { theme, isDark, toggleTheme } = useTheme();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingOffice, setEditingOffice] = useState<ExchangeOffice | null>(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [form] = Form.useForm();

  // Loading states for buttons
  const [loadingEditOfficeId, setLoadingEditOfficeId] = useState<number | null>(null);
  const [loadingToggleStatusId, setLoadingToggleStatusId] = useState<number | null>(null);
  const [loadingDeleteOfficeId, setLoadingDeleteOfficeId] = useState<number | null>(null);

  // Upload progress flags to prevent saving before uploads finish
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingVideoOption, setIsUploadingVideoOption] = useState(false);

  // Upload file lists state
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [videoFileList, setVideoFileList] = useState<any[]>([]);
  const [videoOptionFileList, setVideoOptionFileList] = useState<any[]>([]);

  // Redirect if not admin (check localStorage directly)
  useEffect(() => {
    const adminToken = localStorage.getItem('admin-token');
    if (!adminToken) {
      message.warning('يجب تسجيل الدخول كمشرف للوصول لهذه الصفحة');
      navigate('/login');
    }
  }, [navigate]);

  // Load offices from API
  useEffect(() => {
    loadOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOffices = async () => {
    setTableLoading(true);
    try {
      const officesData = await api.getOffices();
      dispatch(setOffices(officesData));
      // Also save to localStorage as backup
      localStorage.setItem('offices', JSON.stringify(officesData));
    } catch (error: any) {
      message.error('فشل في تحميل المكاتب: ' + error.message);
      // Try loading from localStorage as fallback
      const savedOffices = localStorage.getItem('offices');
      if (savedOffices) {
        dispatch(setOffices(JSON.parse(savedOffices)));
      }
    } finally {
      setTableLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.adminLogout();
    } catch (error) {
      // Continue with logout even if API call fails
    }
    dispatch(logout());
    navigate('/login');
  };

  const handleAddOffice = () => {
    setEditingOffice(null);
    form.resetFields();
    // Reset file lists
    setLogoFileList([]);
    setVideoFileList([]);
    setVideoOptionFileList([]);
    setIsModalVisible(true);
  };

  const handleEditOffice = async (office: ExchangeOffice) => {
    setLoadingEditOfficeId(office.id);
    try {
      // استدعاء API لجلب أحدث بيانات المكتب من السيرفر
      const officeData = await api.getOffice(office.id);
  
      setEditingOffice(officeData);

      // افتح المودال مباشرة ليظهر بشكل أسرع للمستخدم
      setIsModalVisible(true);

      // Extract URLs and IDs from media objects
      const logoUrl = getMediaUrl(officeData.logo);
      const videoUrl = getMediaUrl(officeData.video);
      const videoOptionUrl = getMediaUrl(officeData.videoOption);
      const logoId = getMediaId(officeData.logo);
      const videoId = getMediaId(officeData.video);
      const videoOptionId = getMediaId(officeData.videoOption);

      form.setFieldsValue({
        name: officeData.name,
        website: officeData.website,
        accountUsername: officeData.accountUsername,
        accountPassword: '', // Don't show password
        status: officeData.status,
        // Set media IDs for form submission
        logoId: logoId,
        videoId: videoId,
        videoOptionId: videoOptionId,
      });

      // Set existing files for preview
      if (logoUrl) {
        setLogoFileList([{
          uid: '-1',
          name: 'الشعار الحالي',
          status: 'done',
          url: logoUrl,
          thumbUrl: logoUrl,
        }]);
      } else {
        setLogoFileList([]);
      }
      if (videoUrl) {
        setVideoFileList([{
          uid: '-2',
          name: 'الفيديو الحالي',
          status: 'done',
          url: videoUrl,
        }]);
      } else {
        setVideoFileList([]);
      }
      if (videoOptionUrl) {
        setVideoOptionFileList([{
          uid: '-3',
          name: 'خيارات الفيديو الحالية',
          status: 'done',
          url: videoOptionUrl,
        }]);
      } else {
        setVideoOptionFileList([]);
      }
    } catch (error: any) {
      message.error('فشل في تحميل بيانات المكتب: ' + error.message);
    } finally {
      setLoadingEditOfficeId(null);
    }
  };

  const handleDeleteOffice = (id: number) => {
    Modal.confirm({
      title: 'تأكيد الحذف',
      content: 'هل أنت متأكد من حذف هذا الحساب؟ سيتم حذف جميع البيانات المرتبطة به.',
      okText: 'حذف',
      okType: 'danger',
      cancelText: 'إلغاء',
      onOk: async () => {
        setLoadingDeleteOfficeId(id);
        try {
          await api.deleteOffice(id);
          // بعد الحذف، أعد طلب جلب المكاتب
          await loadOffices();
          message.success('تم حذف الحساب بنجاح');
        } catch (error: any) {
          message.error('فشل في حذف الحساب: ' + error.message);
        } finally {
          setLoadingDeleteOfficeId(null);
        }
      },
    });
  };

  const handleToggleStatus = async (office: ExchangeOffice) => {
    setLoadingToggleStatusId(office.id);
    try {
      const newStatus = office?.status === 'active' ? 'inactive' : 'active';
      await api.updateOffice(office.id, { status: newStatus });
      await loadOffices(); // بعد التعديل اعادة طلب المكاتب
      message.success('تم تحديث حالة الحساب');
    } catch (error: any) {
      message.error('فشل في تحديث الحالة: ' + error.message);
    } finally {
      setLoadingToggleStatusId(null);
    }
  };

  const handleViewDisplay = (
    officeId: number,
    mode: 1 | 2 | 3 | 4,
    linkVersion: 'old' | 'new',
  ) => {
    const oldQueries: Record<1 | 2 | 3 | 4, string> = {
      1: 'h=1&theme=dark',
      2: 'h=1&theme=light',
      3: 'v=1&theme=dark',
      4: 'v=1&theme=light',
    };
    const query = linkVersion === 'old' ? oldQueries[mode] : `m=${mode}`;
    window.open(`/display/${officeId}?${query}`, '_blank');
  };

  const handleManageOffice = (officeId: number) => {
    navigate(`/admin/office/${officeId}`);
  };

  const handleSaveOffice = async () => {
    // Prevent saving while any media is still uploading
    if (isUploadingLogo || isUploadingVideo || isUploadingVideoOption) {
      message.warning('يرجى الانتظار حتى يكتمل رفع الملفات قبل الحفظ');
      return;
    }

    try {
      const values = await form.validateFields();
      setLoading(true);

      // Build request matching API validation
      const officeRequest: ExchangeOfficeRequest = {
        name: values.name,
        website: values.website || null,
        accountUsername: values.accountUsername,
        account_username: values.accountUsername,
        status: values.status || 'active',
        // Media IDs
        logoId: values.logoId || null,
        logo_id: values.logoId || null,
        videoId: values.videoId || null,
        video_id: values.videoId || null,
        videoOptionId: values.videoOptionId || null,
        video_option_id: values.videoOptionId || null,
      };

      // Only include password if provided
      if (values.accountPassword) {
        officeRequest.accountPassword = values.accountPassword;
        officeRequest.account_password = values.accountPassword;
      }

      if (editingOffice) {
        // Update existing office ثم إعادة جلب المكاتب من الـ API مثل تغيير الحالة
        await api.updateOffice(editingOffice.id, officeRequest);
        await loadOffices();
        message.success('تم تحديث الحساب بنجاح');
      } else {
        // Create new office - password required
        if (!values.accountPassword) {
          message.error('يرجى إدخال كلمة المرور');
          setLoading(false);
          return;
        }
        await api.createOffice(officeRequest);
        // بعد الإضافة بنجاح، أعد طلب جلب المكاتب
        await loadOffices();
        message.success('تم إضافة الحساب بنجاح');
      }

      setIsModalVisible(false);
      form.resetFields();
      // Reset file lists
      setLogoFileList([]);
      setVideoFileList([]);
      setVideoOptionFileList([]);
    } catch (error: any) {
      message.error('فشل في حفظ الحساب: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'اسم المكتب',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'اللوغو',
      dataIndex: 'logo',
      key: 'logo',
      align: "center",
      render: (logo: any) =>
        logo && logo.url ? (
          <img
            src={logo.url}
            alt="Logo"
            style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid #ddd' }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: 'الموقع',
      dataIndex: 'website',
      key: 'website',
      render: (text: string) =>
        text ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> {text.substring(0, 30)}
            {text.length > 30 ? "..." : ""}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: 'الحالة',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'مفعل' : 'معطل'}
        </Tag>
      ),
    },
    {
      title: 'الإجراءات',
      key: 'actions',
      align: 'center',
      render: (_: any, record: ExchangeOffice) => (
        <Space wrap style={{ justifyContent: 'center', width: '100%', display: 'flex' }}>
          <Tooltip title="تعديل الحساب">
            <Button
              shape="circle"
              icon={loadingEditOfficeId === record.id ? <LoadingOutlined /> : <EditOutlined />}
              onClick={() => handleEditOffice(record)}
              loading={loadingEditOfficeId === record.id}
              disabled={loadingEditOfficeId === record.id}
              size="small"
              style={{
                background: isDark ? theme.surfaceLight : 'transparent',
                color: isDark ? theme.text : theme.text,
                borderColor: isDark ? theme.border : theme.border,
              }}
            />
          </Tooltip>
          <Tooltip title={record?.status === 'active' ? "تعطيل الحساب" : "تفعيل الحساب"}>
            <Button
              shape="circle"
              icon={loadingToggleStatusId === record.id ? <LoadingOutlined /> : (record?.status === 'active' ? <CloseCircleOutlined /> : <CheckCircleOutlined />)}
              onClick={() => handleToggleStatus(record)}
              loading={loadingToggleStatusId === record.id}
              disabled={loadingToggleStatusId === record.id}
              size="small"
              type={record?.status === 'active' ? 'default' : 'primary'}
              style={{
                background: isDark ? theme.surfaceLight : 'transparent',
                color: isDark ? (record?.status === 'active' ? theme.text : theme.primary) : theme.text,
                borderColor: isDark ? theme.border : theme.border,
              }}
            />
          </Tooltip>
          <Tooltip title="إدارة المكتب (عملات، إعدادات)">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleManageOffice(record.id)}
              size="small"
              type="primary"
              style={{
                background: isDark ? theme.primary : 'transparent',
                color: isDark ? theme.text : theme.primary,
                borderColor: isDark ? theme.primary : theme.primary,
              }}
            />
          </Tooltip>
          <Tooltip title="عرضي غامق">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'old', label: 'الرابط القديم', onClick: () => handleViewDisplay(record.id, 1, 'old') },
                  { key: 'new', label: 'الرابط الجديد (m=1)', onClick: () => handleViewDisplay(record.id, 1, 'new') },
                ],
              }}
            >
              <Button
                shape="circle"
                icon={<ColumnWidthOutlined />}
                size="small"
                type="primary"
                style={{
                  background: isDark ? theme.primary : 'transparent',
                  color: isDark ? theme.text : theme.primary,
                  borderColor: isDark ? theme.primary : theme.primary,
                }}
              />
            </Dropdown>
          </Tooltip>
          <Tooltip title="عرضي فاتح">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'old', label: 'الرابط القديم', onClick: () => handleViewDisplay(record.id, 2, 'old') },
                  { key: 'new', label: 'الرابط الجديد (m=2)', onClick: () => handleViewDisplay(record.id, 2, 'new') },
                ],
              }}
            >
              <Button
                shape="circle"
                icon={<ColumnWidthOutlined />}
                size="small"
                type="primary"
                style={{
                  background: isDark ? theme.primary : 'transparent',
                  color: isDark ? theme.text : theme.primary,
                  borderColor: isDark ? theme.primary : theme.primary,
                }}
              />
            </Dropdown>
          </Tooltip>
          <Tooltip title="طولي غامق">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'old', label: 'الرابط القديم', onClick: () => handleViewDisplay(record.id, 3, 'old') },
                  { key: 'new', label: 'الرابط الجديد (m=3)', onClick: () => handleViewDisplay(record.id, 3, 'new') },
                ],
              }}
            >
              <Button
                shape="circle"
                icon={<MenuOutlined />}
                size="small"
                type="primary"
                style={{
                  background: isDark ? theme.primary : 'transparent',
                  color: isDark ? theme.text : theme.primary,
                  borderColor: isDark ? theme.primary : theme.primary,
                }}
              />
            </Dropdown>
          </Tooltip>
          <Tooltip title="طولي فاتح">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'old', label: 'الرابط القديم', onClick: () => handleViewDisplay(record.id, 4, 'old') },
                  { key: 'new', label: 'الرابط الجديد (m=4)', onClick: () => handleViewDisplay(record.id, 4, 'new') },
                ],
              }}
            >
              <Button
                shape="circle"
                icon={<MenuOutlined />}
                size="small"
                type="primary"
                style={{
                  background: isDark ? theme.primary : 'transparent',
                  color: isDark ? theme.text : theme.primary,
                  borderColor: isDark ? theme.primary : theme.primary,
                }}
              />
            </Dropdown>
          </Tooltip>
          <Tooltip title="حذف الحساب">
            <Button
              shape="circle"
              danger
              icon={loadingDeleteOfficeId === record.id ? <LoadingOutlined /> : <DeleteOutlined />}
              onClick={() => handleDeleteOffice(record.id)}
              loading={loadingDeleteOfficeId === record.id}
              disabled={loadingDeleteOfficeId === record.id}
              size="small"
              style={{
                background: isDark ? theme.surfaceLight : 'transparent',
                color: isDark ? theme.error : theme.error,
                borderColor: isDark ? theme.border : theme.error,
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Ant Design dark theme Table fix
  const tableThemeStyle = isDark
    ? {
      background: theme.surface,
      color: theme.text,
      borderColor: theme.border,
    }
    : {
      background: theme.background,
      color: theme.text,
      borderColor: theme.border,
    };

  return (
    <Layout style={{ minHeight: '100vh', background: theme.background }}>
      <Header
        style={{
          background: theme.surface,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 24px',
        }}
      >
        <Title level={3} style={{ color: theme.text, margin: 0 }}>
          لوحة الإدارة المركزية
        </Title>
        <Space>
          <Tooltip title={isDark ? "وضع النهار" : "وضع الليل"}>
            <Button
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              style={{
                background: theme.surfaceLight,
                borderColor: theme.border,
                color: theme.text,
              }}
            />
          </Tooltip>
          <Tooltip title="إضافة حساب صرافة جديد">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddOffice}
              style={{ background: theme.primary, borderColor: theme.primary }}
            >
              إضافة حساب صرافة جديد
            </Button>
          </Tooltip>
          <Tooltip title="تسجيل الخروج">
            <Button icon={<LogoutOutlined />} onClick={handleLogout} danger>
              تسجيل الخروج
            </Button>
          </Tooltip>
        </Space>
      </Header>
      <Content style={{ padding: "24px", background: theme.background }}>
        <Spin spinning={tableLoading}>
          <Card style={{ background: theme.surface, borderColor: theme.border }}>
            <Table
              dataSource={offices}
              columns={columns as ColumnType<ExchangeOffice>[]}
              rowKey="id"
              pagination={{
                pageSize: 10,
                position: ["bottomCenter"],
                style: { textAlign: "center" },
              }}
              style={tableThemeStyle}
              // Table header/body colors for dark mode
              className={isDark ? "ant-table-dark" : ""}
            />
          </Card>
        </Spin>
      </Content>

      <Modal
        title={editingOffice ? "تعديل حساب الصرافة" : "إضافة حساب صرافة جديد"}
        open={isModalVisible}
        onOk={handleSaveOffice}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={700}
        okText={editingOffice ? "تحديث" : "إضافة"}
        cancelText="إلغاء"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="اسم المكتب"
            rules={[
              { required: true, message: "يرجى إدخال اسم المكتب" },
              { max: 255, message: "اسم المكتب يجب أن لا يتجاوز 255 حرف" },
              {
                pattern: /^[A-Za-z0-9\s_-]+$/,
                message: "يرجى استخدام حروف إنجليزية وأرقام فقط (بدون أحرف عربية)",
              },
            ]}
          >
            <Input
              placeholder="مثال: مكتب الصرافة المركزي"
              onChange={(e) => {
                const name = e.target.value;
                // توليد رابط آمن يعتمد على حروف لاتينية فقط
                const normalized = name.toLowerCase().replace(/\s+/g, '');
                const asciiSlug = normalized.replace(/[^a-z0-9]/g, '');
                const safeSubdomain = asciiSlug || 'office';
                const websiteValue = name
                  ? `https://${safeSubdomain}.screen-rates.com`
                  : '';
                // تعبئة الموقع واسم المستخدم تلقائياً
                const currentUsername = form.getFieldValue('accountUsername');
                const nextValues: any = { website: websiteValue };
                // في حالة الإضافة أو إذا كان اسم المستخدم فارغاً، عين اسم المستخدم من الـ slug
                if (!editingOffice || !currentUsername) {
                  nextValues.accountUsername = safeSubdomain;
                }
                form.setFieldsValue(nextValues);
              }}
            />
          </Form.Item>

          <Form.Item
            name="website"
            label="الموقع الإلكتروني (يُملأ تلقائياً ولا يمكن تعديله)"
            rules={[
              { type: "url", message: "يرجى إدخال رابط صحيح" },
              { max: 255, message: "الرابط يجب أن لا يتجاوز 255 حرف" },
            ]}
          >
            <Input placeholder="https://اسمالمكتب.screen-rates.com" prefix={<LinkOutlined />} disabled />
          </Form.Item>

          <Form.Item
            name="accountUsername"
            label="اسم المستخدم"
            rules={[
              { required: true, message: "يرجى إدخال اسم المستخدم" },
              { max: 255, message: "اسم المستخدم يجب أن لا يتجاوز 255 حرف" },
            ]}
          >
            <Input placeholder="اسم المستخدم لتسجيل الدخول" />
          </Form.Item>

          <Form.Item
            name="accountPassword"
            label={
              editingOffice
                ? "كلمة المرور الجديدة (اتركها فارغة للإبقاء على القديمة)"
                : "كلمة المرور"
            }
            rules={[
              { required: !editingOffice, message: "يرجى إدخال كلمة المرور" },
              { min: 8, message: "كلمة المرور يجب أن تكون 8 أحرف على الأقل" },
              { max: 255, message: "كلمة المرور يجب أن لا تتجاوز 255 حرف" },
            ]}
          >
            <Input.Password
              placeholder={editingOffice ? "••••••••" : "كلمة المرور"}
            />
          </Form.Item>

          {/* Logo Upload */}
          <Form.Item
            name="logoId"
            label={<span style={{ fontWeight: 'bold' }}>شعار المكتب</span>}
          >
            <div style={{
              border: `2px dashed ${theme.border}`,
              borderRadius: 8,
              padding: 16,
              background: isDark ? theme.surfaceLight : '#fafafa',
              textAlign: 'center'
            }}>
              {logoFileList.length > 0 && logoFileList[0].url && (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={logoFileList[0].url}
                    alt="الشعار"
                    style={{
                      maxWidth: 200,
                      maxHeight: 150,
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`,
                      objectFit: 'contain'
                    }}
                  />
                  <div style={{ marginTop: 8, color: theme.textSecondary, fontSize: 12 }}>
                    {logoFileList[0].name}
                  </div>
                </div>
              )}
              <Upload
                name="medium"
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.bmp"
                listType="text"
                showUploadList={false}
                fileList={logoFileList}
                customRequest={async (options) => {
                  try {
                    setIsUploadingLogo(true);
                    const file = options.file as RcFile;
                    const validation = api.validateMediaFile(file);
                    if (!validation.valid) {
                      message.error(validation.error);
                      options.onError?.(new Error(validation.error));
                      return;
                    }
                    const res = await api.uploadMedia(file, api.MediumFor.LOGO);
                    form.setFieldsValue({ logoId: res.id });
                    setLogoFileList([{
                      uid: '-1',
                      name: file.name,
                      status: 'done',
                      url: res.url,
                      thumbUrl: res.url,
                    }]);
                    options.onSuccess?.(res);
                    message.success("تم رفع الشعار بنجاح");
                  } catch (e: any) {
                    message.error(e.message || "فشل رفع الشعار");
                    options.onError?.(e);
                  } finally {
                    setIsUploadingLogo(false);
                  }
                }}
                beforeUpload={(file) => {
                  const ext = file.name.split('.').pop()?.toLowerCase() || '';
                  const isImage = api.ALLOWED_IMAGE_EXTENSIONS.includes(ext);
                  if (!isImage) {
                    message.error(`يرجى رفع صورة فقط! الأنواع المسموحة: ${api.ALLOWED_IMAGE_EXTENSIONS.join(', ')}`);
                    return Upload.LIST_IGNORE;
                  }
                  // if (file.size > api.MAX_FILE_SIZE) {
                  //   message.error('حجم الملف يتجاوز الحد المسموح (25 ميجابايت)');
                  //   return Upload.LIST_IGNORE;
                  // }
                  return true;
                }}
                maxCount={1}
              >
                <Button icon={<PlusOutlined />} size="small" type="dashed" style={{ marginBottom: 8, marginLeft: 8 }}>
                  {logoFileList.length > 0 ? 'تغيير الشعار' : 'رفع الشعار'}
                </Button>
              </Upload>
              {logoFileList.length > 0 && (
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    setLogoFileList([]);
                    form.setFieldsValue({ logoId: undefined });
                  }}
                  style={{ marginTop: 8 }}
                >
                  حذف الشعار
                </Button>
              )}
              {isUploadingLogo && (
                <div style={{ color: theme.info, fontSize: 11, marginTop: 8 }}>
                  جاري رفع الشعار... يرجى الانتظار قبل الحفظ
                </div>
              )}
              <div style={{ color: theme.textMuted, fontSize: 11, marginTop: 8 }}>
                الأنواع المسموحة: JPG, PNG, GIF, WEBP, SVG (حد أقصى 25 ميجابايت)
              </div>
            </div>
          </Form.Item>

          {/* Video Upload */}
          <Form.Item
            name="videoId"
            label={<span style={{ fontWeight: 'bold' }}>فيديو تعريفي (اختياري)</span>}
          >
            <div style={{
              border: `2px dashed ${theme.border}`,
              borderRadius: 8,
              padding: 16,
              background: isDark ? theme.surfaceLight : '#fafafa',
              textAlign: 'center'
            }}>
              {videoFileList.length > 0 && videoFileList[0].url && (
                <div style={{ marginBottom: 16 }}>
                  <video
                    src={videoFileList[0].url}
                    style={{
                      maxWidth: 300,
                      maxHeight: 200,
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`
                    }}
                    controls
                  />
                  <div style={{ marginTop: 8, color: theme.textSecondary, fontSize: 12 }}>
                    {videoFileList[0].name}
                  </div>
                </div>
              )}
              <Upload
                name="medium"
                accept=".mp4,.avi,.mov,.wmv,.webm,.mkv,.mpeg,.mpg,.flv"
                listType="text"
                showUploadList={false}
                fileList={videoFileList}
                customRequest={async (options) => {
                  try {
                    setIsUploadingVideo(true);
                    const file = options.file as RcFile;
                    const validation = api.validateMediaFile(file);
                    if (!validation.valid) {
                      message.error(validation.error);
                      options.onError?.(new Error(validation.error));
                      return;
                    }
                    const res = await api.uploadMedia(file, api.MediumFor.VIDEO);
                    form.setFieldsValue({ videoId: res.id });
                    setVideoFileList([{
                      uid: '-2',
                      name: file.name,
                      status: 'done',
                      url: res.url,
                    }]);
                    options.onSuccess?.(res);
                    message.success("تم رفع الفيديو بنجاح");
                  } catch (e: any) {
                    message.error(e.message || "فشل رفع الفيديو");
                    options.onError?.(e);
                  } finally {
                    setIsUploadingVideo(false);
                  }
                }}
                beforeUpload={(file) => {
                  const ext = file.name.split('.').pop()?.toLowerCase() || '';
                  const isVideo = api.ALLOWED_VIDEO_EXTENSIONS.includes(ext);
                  if (!isVideo) {
                    message.error(`يرجى رفع ملف فيديو فقط! الأنواع المسموحة: ${api.ALLOWED_VIDEO_EXTENSIONS.join(', ')}`);
                    return Upload.LIST_IGNORE;
                  }
                  return true;
                }}
                maxCount={1}
              >
                <Button icon={<VideoCameraOutlined />} type="dashed" size="small" style={{ marginBottom: 8, marginLeft: 8 }}>
                  {videoFileList.length > 0 ? 'تغيير الفيديو' : 'رفع الفيديو'}
                </Button>
              </Upload>
              {videoFileList.length > 0 && (
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    setVideoFileList([]);
                    form.setFieldsValue({ videoId: undefined });
                  }}
                  style={{ marginTop: 8 }}
                >
                  حذف الفيديو
                </Button>
              )}
              {isUploadingVideo && (
                <div style={{ color: theme.info, fontSize: 11, marginTop: 8 }}>
                  جاري رفع الفيديو... يرجى الانتظار قبل الحفظ
                </div>
              )}
              <div style={{ color: theme.textMuted, fontSize: 11, marginTop: 8 }}>
                الأنواع المسموحة: MP4, AVI, MOV, WEBM, MKV
              </div>
            </div>
          </Form.Item>

          {/* Video Option Upload */}
          <Form.Item
            name="videoOptionId"
            label={<span style={{ fontWeight: 'bold' }}>خيارات الفيديو (اختياري)</span>}
          >
            <div style={{
              border: `2px dashed ${theme.border}`,
              borderRadius: 8,
              padding: 16,
              background: isDark ? theme.surfaceLight : '#fafafa',
              textAlign: 'center'
            }}>
              {videoOptionFileList.length > 0 && videoOptionFileList[0].url && (
                <div style={{ marginBottom: 16 }}>
                  {videoOptionFileList[0].url.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) ? (
                    <img
                      src={videoOptionFileList[0].url}
                      alt="خيارات الفيديو"
                      style={{
                        maxWidth: 200,
                        maxHeight: 150,
                        borderRadius: 8,
                        border: `1px solid ${theme.border}`,
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <video
                      src={videoOptionFileList[0].url}
                      style={{
                        maxWidth: 300,
                        maxHeight: 200,
                        borderRadius: 8,
                        border: `1px solid ${theme.border}`
                      }}
                      controls
                    />
                  )}
                  <div style={{ marginTop: 8, color: theme.textSecondary, fontSize: 12 }}>
                    {videoOptionFileList[0].name}
                  </div>
                </div>
              )}
              <Upload
                name="medium"
                accept=".mp4,.avi,.mov,.wmv,.webm,.mkv,.mpeg,.mpg,.flv,.jpg,.jpeg,.png,.gif,.webp"
                listType="text"
                showUploadList={false}
                fileList={videoOptionFileList}
                customRequest={async (options) => {
                  try {
                    setIsUploadingVideoOption(true);
                    const file = options.file as RcFile;
                    const validation = api.validateMediaFile(file, { skipSizeCheck: true });
                    if (!validation.valid) {
                      message.error(validation.error);
                      options.onError?.(new Error(validation.error));
                      return;
                    }
                    const res = await api.uploadMedia(file, api.MediumFor.GENERAL);
                    form.setFieldsValue({ videoOptionId: res.id });
                    setVideoOptionFileList([{
                      uid: '-3',
                      name: file.name,
                      status: 'done',
                      url: res.url,
                    }]);
                    options.onSuccess?.(res);
                    message.success("تم رفع الملف بنجاح");
                  } catch (e: any) {
                    message.error(e.message || "فشل رفع الملف");
                    options.onError?.(e);
                  } finally {
                    setIsUploadingVideoOption(false);
                  }
                }}
                beforeUpload={(file) => {
                  const ext = file.name.split('.').pop()?.toLowerCase() || '';
                  const isAllowed = api.ALL_ALLOWED_EXTENSIONS.includes(ext);
                  if (!isAllowed) {
                    message.error(`نوع الملف غير مدعوم! الأنواع المسموحة: ${api.ALL_ALLOWED_EXTENSIONS.join(', ')}`);
                    return Upload.LIST_IGNORE;
                  }
                  return true;
                }}
                maxCount={1}
              >
                <Button icon={<PlusOutlined />} size="small" type="dashed" style={{ marginBottom: 8, marginLeft: 8 }}>
                  {videoOptionFileList.length > 0 ? 'تغيير الملف' : 'رفع خيارات الفيديو'}
                </Button>
              </Upload>
              {videoOptionFileList.length > 0 && (
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    setVideoOptionFileList([]);
                    form.setFieldsValue({ videoOptionId: undefined });
                  }}
                  style={{ marginTop: 8 }}
                >
                  حذف الملف
                </Button>
              )}
              {isUploadingVideoOption && (
                <div style={{ color: theme.info, fontSize: 11, marginTop: 8 }}>
                  جاري رفع الملف... يرجى الانتظار قبل الحفظ
                </div>
              )}
              <div style={{ color: theme.textMuted, fontSize: 11, marginTop: 8 }}>
                الأنواع المسموحة: صور وفيديو
              </div>
            </div>
          </Form.Item>

          <Form.Item name="status" label="الحالة" initialValue="active">
            <Select>
              <Option value="active">مفعل</Option>
              <Option value="inactive">معطل</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      {/* Dark Table CSS (can be moved to CSS file if preferred) */}
      <style>{`
        .ant-table-dark .ant-table {
            background: ${theme.surface} !important;
            color: ${theme.text} !important;
        }
        .ant-table-dark .ant-table-thead > tr > th {
            background: ${theme.surfaceLight} !important;
            color: ${theme.text} !important;
            border-bottom: 1px solid ${theme.border} !important;
        }
        .ant-table-dark .ant-table-tbody > tr > td {
            background: ${theme.surface} !important;
            color: ${theme.text} !important;
        }
        .ant-table-dark .ant-table-pagination {
            background: ${theme.surface} !important;
            color: ${theme.text} !important;
        }
      `}</style>
    </Layout>
  );
};

export default CentralAdmin;
